import {env} from "../env/env";
import {DesignView} from "../components/dsa/EditScreenContentModal";
import {DesignDto} from "../generated/dashboard-client";

export const PAIR_NEW_DEVICE = 'PAIR_NEW_DEVICE';

export function getLocationDesignsPerPage(data: DesignDto[], locationId?: number) {
  const bits = [] as DesignView[];
  data.forEach(it => {
    it.pageCount = it.pageCount ?? 1;

    for (let page = 1; page <= it.pageCount; page++) {
      bits.push({
        id: it.id ?? "",
        size: it.size ?? "",
        name: it.name ?? "",
        page,
        thumbnail: `${env.rendererPath}/design/thumbnail/${it.id}?height=${env.rendererImageHeight}&update=${(new Date((it.updatedDate!)).getTime())}&page=${page}`,
        selected: false,
        visible: true,
        width: it.width ?? "",
        height: it.height ?? "",
        unit: it.unit ?? "",
        locationId
      });
    }
  });
  return bits;
}

export function getMultiLocationDesignsPerPage(data: { [key: string]: DesignDto[] }) {
  return Object.entries(data).flatMap(([locationId, designs]) =>
    getLocationDesignsPerPage(designs, Number(locationId))
  );
}

export async function onPairNewDevice() {
  document.dispatchEvent(new CustomEvent(PAIR_NEW_DEVICE));
}

export interface NamedEntity {
  id?: string;
  name?: string;
  removed?: boolean;
}

function findLive(entities: NamedEntity[], id: string | undefined) {
  return entities.find(e => e.id === id && !e.removed);
}

interface BoardLike {
  type?: string;
  name?: string;
  designId?: string;
  uploadId?: string;
}

interface EventLike {
  name?: string | null;
  content?: string;
  contentMeta?: string;
  contentTitle?: string;
}

export function resolveBoardName(
  board: BoardLike | undefined,
  designs: NamedEntity[],
  uploads: NamedEntity[]
): string {
  if (!board) return "";
  if (board.type === "UPLOAD") {
    return findLive(uploads, board.uploadId)?.name ?? board.name ?? "";
  }
  return findLive(designs, board.designId)?.name ?? board.name ?? "";
}

export function resolveContentName(
  content: string | undefined,
  contentMeta: string | undefined,
  designs: NamedEntity[],
  uploads: NamedEntity[],
  playlists: NamedEntity[]
): string | undefined {
  if (content === "design") return findLive(designs, contentMeta)?.name;
  if (content === "upload") return findLive(uploads, contentMeta)?.name;
  if (content === "playlist") return findLive(playlists, contentMeta)?.name;
  return undefined;
}

export function resolveEventTitle(
  event: EventLike,
  designs: NamedEntity[],
  uploads: NamedEntity[],
  playlists: NamedEntity[]
): string {
  if (event.name) return event.name;
  return resolveContentName(event.content, event.contentMeta, designs, uploads, playlists)
    ?? event.contentTitle ?? "";
}

export const NO_ASSET_LABEL = "Nothing is assigned yet";

export function isEventContentDeleted(
  event: EventLike,
  designs: NamedEntity[],
  uploads: NamedEntity[],
  playlists: NamedEntity[],
  loaded: boolean
): boolean {
  if (!loaded) return false;
  if (!event.content || event.content === "screensaver" || !event.contentMeta) return false;
  return resolveContentName(event.content, event.contentMeta, designs, uploads, playlists) === undefined;
}

export function displayEventTitle(
  event: EventLike,
  designs: NamedEntity[],
  uploads: NamedEntity[],
  playlists: NamedEntity[],
  loaded: boolean
): string {
  if (isEventContentDeleted(event, designs, uploads, playlists, loaded)) return NO_ASSET_LABEL;
  return resolveEventTitle(event, designs, uploads, playlists);
}

export function isBoardContentDeleted(
  board: BoardLike | undefined,
  designs: NamedEntity[],
  uploads: NamedEntity[],
  loaded: boolean
): boolean {
  if (!loaded || !board) return false;
  if (board.type === "UPLOAD") {
    return !!board.uploadId && !findLive(uploads, board.uploadId);
  }
  return !!board.designId && !findLive(designs, board.designId);
}

export function displayBoardName(
  board: BoardLike | undefined,
  designs: NamedEntity[],
  uploads: NamedEntity[],
  loaded: boolean
): string {
  if (isBoardContentDeleted(board, designs, uploads, loaded)) return NO_ASSET_LABEL;
  return resolveBoardName(board, designs, uploads);
}
